<template>
	<div style="overflow-anchor: none;">
		<slot />

		<slot v-if="empty" name="no-items" />
		<slot v-else-if="complete" name="complete" />
		<slot v-else name="spinner" />

		<div ref="target" style="height: 1px;" />
	</div>
</template>

<script setup>
import { useElementVisibility } from '@vueuse/core'
import { nextTick, onBeforeUnmount, onDeactivated, ref, useTemplateRef, watch } from 'vue';

// Refs
const target = useTemplateRef('target')
const targetIsVisible = useElementVisibility(target)
let isRunning = false // Prevents multiple watch executions from overlapping

// Props
const props = defineProps({
	handler: {
		type: Function,
		required: true
	},
	delay: {
		type: Number,
		default: 500
	}
})

defineExpose({
	reset
})

// Reactives
const empty = ref(false)
const complete = ref(false)
const active = ref(true)

const state = {
	complete: () => {
		complete.value = true
	},
	empty: () => {
		empty.value = true
		complete.value = true
	},
}

// Emit event onShow if the element reached the viewport
watch(targetIsVisible, load)

// Methods
async function load(){
	// Only trigger execution sequence if visible, not complete, active, and not already running
	if (!targetIsVisible.value || complete.value || !active.value || isRunning) return

	isRunning = true

	try {
		do {
			// Wait for a given number of milliseconds
			await new Promise(resolve => setTimeout(resolve, props.delay))
			
			// Call actual loader
			await props.handler(state)

			// 1. Wait for Vue to update the DOM rows
			await nextTick()
			
			// 2. Yield execution control completely to the browser layout engine.
			// This allows the IntersectionObserver/VueUse to update targetIsVisible.value
			await new Promise(resolve => setTimeout(resolve, 50))

		} while (targetIsVisible.value && !complete.value && active.value)
	} finally {
		isRunning = false
	}
}

function reset(){
	complete.value = false
	empty.value = false

	// Force reload
	load()
}

// Lifecycle
onDeactivated(() => {
	active.value = false
})

onBeforeUnmount(() => {
	active.value = false
})
</script>